/**
 * Pay-cycle month utilities
 *
 * Pay-cycle months run from pay day (~15th) of one month to the day before
 * next pay day. When the 15th falls on a weekend, pay arrives on the Friday before.
 *
 * A month key (e.g., "2026-01") represents transactions from Jan pay day to
 * the day before Feb pay day.
 */

/** Matches a well-formed pay-cycle month key, e.g. "2026-01". */
const MONTH_KEY_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * Whether a string is a valid pay-cycle month key (YYYY-MM, month 01–12).
 * Shared by every route that reads the `?month=` query param so an arbitrary
 * URL value can't flow into the date maths and produce "NaN-NaN" keys.
 */
export function isValidMonthKey(value: string | null | undefined): value is string {
	return typeof value === 'string' && MONTH_KEY_RE.test(value);
}

/**
 * Get the effective pay day for a given month, accounting for weekends.
 * If the 15th is Saturday, pay is Friday the 14th.
 * If the 15th is Sunday, pay is Friday the 13th.
 * @param year - Full year (e.g., 2026)
 * @param month - 1-indexed month (1 = January)
 */
export function getEffectivePayDay(year: number, month: number): number {
	const fifteenth = new Date(year, month - 1, 15);
	const dayOfWeek = fifteenth.getDay(); // 0 = Sunday, 6 = Saturday
	if (dayOfWeek === 6) return 14; // Saturday -> Friday 14th
	if (dayOfWeek === 0) return 13; // Sunday -> Friday 13th
	return 15;
}

/**
 * Today's calendar date in NZ (Pacific/Auckland), independent of the browser's
 * timezone. The Go backend resolves pay cycles in Pacific/Auckland, so "now"
 * here must too — otherwise a browser in another timezone could land on a
 * different cycle near midnight. (The weekend/range maths above is pure calendar
 * arithmetic and already timezone-safe.)
 */
function nzToday(): { year: number; month: number; day: number } {
	const parts = new Intl.DateTimeFormat('en-NZ', {
		timeZone: 'Pacific/Auckland',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	}).formatToParts(new Date());
	const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
	return { year: get('year'), month: get('month'), day: get('day') };
}

/**
 * Get the current pay-cycle month in YYYY-MM format.
 * If before this month's effective pay day, returns the previous month.
 */
export function getCurrentMonth(): string {
	const { year, month, day } = nzToday();
	const effectivePayDay = getEffectivePayDay(year, month);
	if (day < effectivePayDay) {
		const prevMonth = month === 1 ? 12 : month - 1;
		const prevYear = month === 1 ? year - 1 : year;
		return `${prevYear}-${String(prevMonth).padStart(2, '0')}`;
	}
	return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Format a pay-cycle month for display.
 * Shows the date range accounting for weekend adjustments.
 * Example: "15 Jan - 12 Feb 2026" (if Feb 15 is Sunday, pay is 13th, so cycle ends 12th)
 */
export function formatMonth(yearMonth: string): string {
	const { start, end } = getPayCycleRange(yearMonth);

	const startStr = start.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
	const endStr = end.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
	return `${startStr} - ${endStr}`;
}

/**
 * Navigate to a different pay-cycle month by offset.
 * @param currentMonth - Current month in YYYY-MM format
 * @param offset - Number of months to move (negative for past, positive for future)
 * @returns New month in YYYY-MM format
 */
export function changeMonth(currentMonth: string, offset: number): string {
	const [year, month] = currentMonth.split('-').map(Number);
	const date = new Date(year, month - 1 + offset);
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Get the pay-cycle start/end dates for a month key (YYYY-MM).
 * Start is the effective pay day for that month; end is the day before the next month's pay day.
 */
export function getPayCycleRange(yearMonth: string): { start: Date; end: Date } {
	const [year, month] = yearMonth.split('-').map(Number);
	const startDay = getEffectivePayDay(year, month);
	const start = new Date(year, month - 1, startDay);

	const nextYear = month === 12 ? year + 1 : year;
	const nextMonth = month === 12 ? 1 : month + 1;
	const nextPayDay = getEffectivePayDay(nextYear, nextMonth);
	const end = new Date(nextYear, nextMonth - 1, nextPayDay - 1);

	return { start, end };
}

export interface PayCycleProgress {
	/** Percentage through the pay cycle (0-100) */
	percent: number;
	/** Current day number within the cycle (1-indexed) */
	dayNumber: number;
	/** Total days in the cycle */
	totalDays: number;
	/** Days remaining in the cycle */
	daysRemaining: number;
	/** Days until the cycle starts (only relevant for future cycles) */
	daysUntilStart: number;
	/** Whether the selected month is in the past */
	isPast: boolean;
	/** Whether the selected month is in the future */
	isFuture: boolean;
	/** Whether this is the current pay cycle */
	isCurrent: boolean;
}

/**
 * Get progress information for a pay cycle.
 * @param yearMonth - Month key in YYYY-MM format
 * @returns Progress information including percentage, day counts, and temporal status
 */
export function getPayCycleProgress(yearMonth: string): PayCycleProgress {
	const { start, end } = getPayCycleRange(yearMonth);
	const today = nzToday();

	// Set times to midnight for accurate day comparisons. "Now" is NZ's calendar
	// day so progress matches the backend's pay-cycle resolution.
	const startMidnight = new Date(start.getFullYear(), start.getMonth(), start.getDate());
	const endMidnight = new Date(end.getFullYear(), end.getMonth(), end.getDate());
	const nowMidnight = new Date(today.year, today.month - 1, today.day);

	// Total days in the cycle (inclusive of both start and end)
	const totalDays = Math.round((endMidnight.getTime() - startMidnight.getTime()) / (1000 * 60 * 60 * 24)) + 1;

	// Determine temporal status
	const isPast = nowMidnight > endMidnight;
	const isFuture = nowMidnight < startMidnight;
	const isCurrent = !isPast && !isFuture;

	if (isPast) {
		return {
			percent: 100,
			dayNumber: totalDays,
			totalDays,
			daysRemaining: 0,
			daysUntilStart: 0,
			isPast: true,
			isFuture: false,
			isCurrent: false,
		};
	}

	if (isFuture) {
		const daysUntilStart = Math.round((startMidnight.getTime() - nowMidnight.getTime()) / (1000 * 60 * 60 * 24));
		return {
			percent: 0,
			dayNumber: 0,
			totalDays,
			daysRemaining: totalDays,
			daysUntilStart,
			isPast: false,
			isFuture: true,
			isCurrent: false,
		};
	}

	// Current cycle - calculate actual progress
	const dayNumber = Math.round((nowMidnight.getTime() - startMidnight.getTime()) / (1000 * 60 * 60 * 24)) + 1;
	const daysRemaining = totalDays - dayNumber;
	const percent = Math.round((dayNumber / totalDays) * 100);

	return {
		percent,
		dayNumber,
		totalDays,
		daysRemaining,
		daysUntilStart: 0,
		isPast: false,
		isFuture: false,
		isCurrent: true,
	};
}
